import {
  Breadcrumbs,
  Button,
  ConfirmDialog,
  DetailPageHeader,
  DetailPageHero,
  PageContainer,
  toast,
  useApiClient,
} from '@startupquickstart/frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ClipboardList, FileText, Gamepad2, Lightbulb, Lock, Pencil, Target, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Outlet, useNavigate, useParams } from 'react-router-dom';

import type { EventChallenge } from '@/config/objects/events';
import { useQrDataUrl } from '@/hooks/useQrDataUrl';

interface EventLite {
  id: string;
  name: string;
}

const tabs = [
  { label: 'Overview', path: 'overview', icon: FileText },
  { label: 'Demo', path: 'demo', icon: Gamepad2 },
  { label: 'Submissions', path: 'submissions', icon: ClipboardList },
] as const;

/**
 * Top-level challenge detail screen — same DetailPageHero shell as
 * the team page so the two surfaces feel like part of the same
 * shape. Access-code QR is the hero image when set; the shared
 * skeleton + fallback handles the no-code case.
 */
export function EventChallengeDetailPage() {
  const { id: eventId, challengeId } = useParams<{ id: string; challengeId: string }>();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const [confirmDelete, setConfirmDelete] = useState(false);

  const challengeQuery = useQuery<{ success: boolean; data: EventChallenge }>({
    queryKey: ['EventChallenge', eventId, challengeId],
    queryFn: () => apiClient.get(`/events/${eventId}/challenges/${challengeId}`),
    enabled: !!eventId && !!challengeId,
  });
  const challenge = challengeQuery.data?.data;

  const eventQuery = useQuery<{ success: boolean; data: EventLite }>({
    queryKey: ['Event', eventId],
    queryFn: () => apiClient.get(`/events/${eventId}`),
    enabled: !!eventId,
  });
  const event = eventQuery.data?.data;

  // Only encode a QR when the challenge has an access code; passing
  // null produces null from the hook and the shared hero falls back
  // to the icon block automatically.
  const qrDataUrl = useQrDataUrl(challenge?.accessCode ?? null, 320);

  const deleteMutation = useMutation({
    mutationFn: () => apiClient.delete(`/events/${eventId}/challenges/${challengeId}`),
    onSuccess: () => {
      toast.success('Challenge deleted');
      queryClient.invalidateQueries({ queryKey: ['EventChallenges', eventId] });
      queryClient.invalidateQueries({ queryKey: ['EventGroups', eventId] });
      navigate(`/events/${eventId}/challenges`);
    },
    onError: (err: Error) => toast.error(err?.message || 'Failed to delete challenge'),
  });

  const basePath = `/events/${eventId}/challenges/${challengeId}`;

  return (
    // Match the event detail page shell — no outer padding so the
    // breadcrumb row sits flush against the hero, no wrapping p-6.
    <PageContainer padding="none" className="flex flex-col overflow-hidden">
      <DetailPageHeader
        actions={
          challenge ? (
            <>
              <Button
                variant="outline"
                onClick={() =>
                  navigate(`/events/${eventId}/challenges/${challengeId}/edit`)
                }>
                <Pencil className="h-4 w-4" />
                Edit
              </Button>
              <Button variant="destructive" onClick={() => setConfirmDelete(true)}>
                <Trash2 className="h-4 w-4" />
                Delete
              </Button>
            </>
          ) : null
        }>
        <Breadcrumbs
          items={[
            { label: 'Home', href: '/' },
            { label: 'Events', href: '/events' },
            { label: event?.name || 'Event', href: `/events/${eventId}/overview` },
            { label: 'Challenges', href: `/events/${eventId}/challenges` },
            { label: challenge?.name || 'Challenge', current: true },
          ]}
        />
      </DetailPageHeader>

      <DetailPageHero
        isLoading={challengeQuery.isLoading}
        category={{ label: 'Challenge', icon: Target }}
        title={challenge?.name || 'Challenge'}
        image={{
          url: qrDataUrl,
          alt: challenge?.accessCode
            ? `QR code for access code ${challenge.accessCode}`
            : 'No access-code QR',
          fallbackIcon: challenge?.accessCode ? Lock : Lightbulb,
          // QR is intrinsically 1:1 — the square hero slot fits it
          // edge-to-edge so the code stays scannable without left/
          // right letterboxing. `contain` is the safety net for the
          // fallback Lock/Lightbulb icon when there's no access code.
          shape: 'square',
          objectFit: 'contain',
        }}
        meta={
          challenge ? (
            <span className="text-sm text-white/70">
              {challenge.points} pts • {challenge.type} • {challenge.difficulty}
              {challenge.accessCode && (
                <>
                  {' '}
                  • Code{' '}
                  <code className="rounded bg-white/10 px-1.5 py-0.5 font-mono">
                    {challenge.accessCode}
                  </code>
                </>
              )}
            </span>
          ) : undefined
        }
        badges={
          challenge?.accessCode ? (
            <span className="flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-900">
              <Lock className="h-3 w-3" />
              Access code
            </span>
          ) : undefined
        }
        tabs={[...tabs]}
        basePath={basePath}>
        {challenge ? (
          <Outlet context={{ challenge, eventId, challengeId }} />
        ) : (
          <div className="px-6 py-8 text-sm text-muted-foreground">Loading challenge…</div>
        )}
      </DetailPageHero>

      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title="Delete challenge?"
        description={`This will permanently delete "${challenge?.name}".`}
        confirmText="Delete"
        variant="destructive"
        loading={deleteMutation.isPending}
        onConfirm={() => deleteMutation.mutate()}
      />
    </PageContainer>
  );
}
